import { useState, useEffect } from 'react';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, X } from 'lucide-react';
import { userService } from "../../../_services";
import PaymentFormNew from "../../PaymentFormNew";
import { GoogleSpreadsheet } from "google-spreadsheet";
import * as z from 'zod';
import { US_STATE_NAMES } from '../../../utils/constants';

const cardDetailsSchema = z.object({
    cardStyle: z.string().min(1, 'Please select card style'),
    quantity: z.string().min(1, 'Please select quantity'),
    groupNumber: z.string().min(1, 'Please select group number'),
});

const shippingSchema = z.object({
    fname: z.string()
        .min(3, 'Minimum 3 characters long')
        .max(20, 'Maximum 20 characters long'),
    lname: z.string()
        .min(2, 'Minimum 2 characters long')
        .max(20, 'Maximum 20 characters long'),
    phone: z.string()
        .refine(val => userService.ValidPhone(val) && val !== '', 'Phone number is not valid'),
    address1: z.string()
        .min(1, 'Please input street address')
        .max(25, 'Please input valid street address'),
    address2: z.string()
        .max(25, 'Please input valid street address'),
    state: z.string().min(1, 'Please select state'),
    city: z.string()
        .min(2, 'Please input city')
        .max(35, 'Please input valid city'),
    zip: z.string().length(5, 'Please input valid zipcode'),
});

// US state names imported from utils/constants as US_STATE_NAMES

const QUANTITY_LIST = [
    { quantity: 100, price: { sq: 24.99, rd: 28.99 } },
    { quantity: 500, price: { sq: 44.99, rd: 52.99 } },
    { quantity: 1000, price: { sq: 59.99, rd: 71.99 } },
    { quantity: 5000, price: { sq: 191.99, rd: 276.99 } },
    { quantity: 10000, price: { sq: 335.99, rd: 499.99 } }
];

function OrderPage({ toggle_user_detail, forCard, groupNumbers }) {
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [fname, setFname] = useState('');
    const [lname, setLname] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [country, setCountry] = useState('United States');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [cardStyle, setCardStyle] = useState('Rounded');
    const [quantity, setQuantity] = useState('100');
    const [groupNumber, setGroupNumber] = useState('');
    const [totalPrice, setTotalPrice] = useState('28.99');
    const [currentStep, setCurrentStep] = useState('card-details'); // 'card-details', 'shipping', 'payment', 'success'
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [errorMsg, setErrorMsg] = useState('');
    const [orderId, setOrderId] = useState('');
    const [orderDate, setOrderDate] = useState('');
    const [orderGroupNumber, setOrderGroupNumber] = useState('');
    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [id, name] = Usermeta_Data.split(':');
        setUserId(id);
        setUserName(name);
        getUserAccountInfo(id, name);

        if (groupNumbers && groupNumbers.length > 0) {
            setGroupNumber(groupNumbers[0].groupnumber);
        }
    }, [groupNumbers]);

    const getUserAccountInfo = async (id, name) => {
        try {
            const apidata = await userService.GetUserAccountInfo(id, name);
            if (apidata.length > 0 && apidata[0].code !== "-1") {
                setFname(apidata[0].fname || '');
                setLname(apidata[0].lname || '');
                setPhone(apidata[0].phone || '');
                setEmail(apidata[0].email || '');
                setAddress1(apidata[0].address1 || '');
                setAddress2(apidata[0].address2 || '');
                // Treat "Please Select State" from DB the same as no value
                const rawState = apidata[0].stateName || '';
                setState(rawState === 'Please Select State' ? '' : rawState);
                setCity(apidata[0].cityName || '');
                setZip(apidata[0].zipCode || '');
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    const handleCardStyleChange = (value) => {
        const cardSt = value.toLowerCase();
        const selectedItem = QUANTITY_LIST.find(item => item.quantity === parseInt(quantity));
        const price = selectedItem ? (cardSt === 'rounded' ? selectedItem.price.rd : selectedItem.price.sq) : 0;
        setCardStyle(value);
        setTotalPrice(price.toString());
    };

    const handleQuantityChange = (value) => {
        const cardSt = cardStyle.toLowerCase();
        const selectedItem = QUANTITY_LIST.find(item => item.quantity === parseInt(value));
        const price = selectedItem ? (cardSt === 'rounded' ? selectedItem.price.rd : selectedItem.price.sq) : 0;
        setQuantity(value);
        setTotalPrice(price.toString());
    };

    const validateCardDetails = () => {
        const result = cardDetailsSchema.safeParse({ cardStyle, quantity, groupNumber });
        if (!result.success) {
            const fe = result.error.flatten().fieldErrors;
            setErrors({
                ...(fe.cardStyle?.[0] && { cardStyle: fe.cardStyle[0] }),
                ...(fe.quantity?.[0] && { quantity: fe.quantity[0] }),
                ...(fe.groupNumber?.[0] && { groupNumber: fe.groupNumber[0] }),
            });
            return false;
        }
        setErrors({});
        return true;
    };

    const validateShipping = () => {
        const result = shippingSchema.safeParse({ fname, lname, phone, address1, address2, state, city, zip });
        if (!result.success) {
            const fe = result.error.flatten().fieldErrors;
            setErrors({
                ...(fe.fname?.[0] && { fname: fe.fname[0] }),
                ...(fe.lname?.[0] && { lname: fe.lname[0] }),
                ...(fe.phone?.[0] && { phone: fe.phone[0] }),
                ...(fe.address1?.[0] && { address1: fe.address1[0] }),
                ...(fe.address2?.[0] && { address2: fe.address2[0] }),
                ...(fe.state?.[0] && { state: fe.state[0] }),
                ...(fe.city?.[0] && { city: fe.city[0] }),
                ...(fe.zip?.[0] && { zip: fe.zip[0] }),
            });
            return false;
        }
        setErrors({});
        return true;
    };

    const handleNextFromCardDetails = () => {
        if (validateCardDetails()) {
            setCurrentStep('shipping');
            setErrors({});
        }
    };

    const handleSaveShipping = () => {
        if (!validateShipping()) return;

        // Save shipping info to profile in the background (non-blocking)
        // The order flow uses local state, so we proceed immediately
        userService.SetUserAccountInfo(
                userId, userName, fname, lname, phone, email,
                address1, address2, country, state, city, zip
        ).catch(() => {
            // Silently ignore � profile update is best-effort
        });

                setCurrentStep('payment');
                setErrors({});
    };

    const handlePaymentSuccess = async () => {
        try {
            const apidata = await userService.GoogleSheetEntry(userId);
                            if (apidata.length > 0) {
                setOrderId(apidata[0].orderId);
                setOrderGroupNumber(apidata[0].groupNumber);
                setOrderDate(apidata[0].date);
                await submitToGoogleSheets(apidata[0].orderId, apidata[0].groupNumber, apidata[0].date);
                setCurrentStep('success');
            }
        } catch (error) {
            console.error('Error processing success:', error);
        }
    };

    const submitToGoogleSheets = async (orderId, gn, date) => {
        const getlogin = window.location.href;
        const isDev = getlogin.toLowerCase().includes("release-backoffice") || 
                      getlogin.toLowerCase().includes("dev-backoffice") || 
                      getlogin.toLowerCase().includes("localhost:") ||
                      getlogin.toLowerCase().includes("shadcn-backoffice");

        const sheetId = isDev ? "1JZS0igMTpHBLZ_la4W0rKvrrb32SNvGAgAjjf3fJ4C8" : "1WdcHyaZGax6Bpo6sY7r6vbRaiOuZBsC-aJkAWntYIHo";
        const clientEmail = isDev ? "google-spread-sheet@cohesive-idiom-332408.iam.gserviceaccount.com" : "paymententry@crafty-key-333107.iam.gserviceaccount.com";
        const privateKey = isDev ? 
            "-----BEGIN PRIVATE KEY-----\nMIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQC/+TBrXzEDSGqQ\nPpXZnhB6kr+h9mz16cntIP1a9w4lWUSmNeThFRy0xAfZOrSBj4BhNkrOmYbVvr47\nwFQg+KurLAqjJg9duJoygR79UPAYjVuiHuHAllXDlr3vHwjPsPs/kBR8uq/5Typ3\n8opPVZGVNgv4D9Jg5I+4457SyJTpN/7GxcpUuWks+k/HpCLRT3zhsDhJdIavQBI3\ngFOn0VpsU1YLC0iKppm07saMv9st2mfBJ4dUHVgAP70Sd8X0b/4rhTh2hveVoDnf\nYUwOy8CgqpLhpkiNgu8nXJh9ozjOPQdwYqpycgoRgYnejD8U31D2gKxCwQ4yGwpK\na1adQPJDAgMBAAECggEACRLEhJajsOWHz9h9aaMsYTa1YvjMLpVFq3T/NIDvxLki\n4v4yGqdOS4bmXhCFvofjR/TydTBRbKHOyPEuqL5IEteKiyp92G6krxVmOw/LwUVR\nGuj9v4X7eLqTsI0VdAeH4Hm1Vi3WqmBg//lRKy2+q0A6MZfB2D2FQ3ZSp+C+FQWw\n4TVRFZVG8TYOIziMuJqkr+UwMYL2OLw6XjqCMloIzgGmZt4gDNSPAR25yJBFVswk\n/Hh4mPXQpNSnn6qQetT7YTqC1inxfg7tZ1aOHXOfs5hCN//v0XPpgx1Tlg0cJiyt\nmm9EdDNg3+fA6BlyfoSwfvng/6TATt3/w/XvogoSCQKBgQDDu6op8crLqXcPDXZ2\nEZdB2GuSSk0eNzpcg6y7OBGjP3cql7SvTkhCOa593zUTDU6bac59E5QIQBD1jGLb\n7Qb8zXwYnjBsipX+BrbMubJaaD3ImsbpcipNsK+zPZsViaE9UbcFSN6Ii3w2lVH/\nkvo12cYWyue6uQUpTrYUH3YMOQKBgQD7FSy113nqS0V8ioRLQPMs5nvkDF1bhKvn\nqpSKr/JdinY7W9Kosc0k4wl/PhZ2tsJH2RuLehDtWNlTa0nWczoLTxXjVTHq314V\nJT38QNS07IRHAvcGEECf5B0dK87t5UoHRPz6tHqU4YAyMq1JDla+qFSZZF1mg26s\nIjR8zRNqWwKBgQCNePIYWRy0MevNJHTwB+zk+dFernepFFAJmF7b/z24WeH/FiHR\n5+oQe/KkJhYNjDu51zMZcnI3uF7JrEu0HFFFnhxJCuWDPnQFZMLxnHLOJ7Qob76/\n8vWiq8eC7U9PtOenE44Ossmttev0496dtJVHIP2tccEmVB7n6W1vhAHl+QKBgDZg\nKpOi+6sq6/yBvw7RHRjfPXtw1hMI03XTcmHqotFxOBO42hFgmC3h4V+mIZVKFR8c\nPKPjJqFQOZigRphj0fE7CmF2RN45y5VGwHrNy4L3Kdp7IOY2RNibvVZg82p/2p1i\namsdBOMTRwEYCoEk0tJtgAzMOJIH6sLqkXVpsEe7AoGBAJb/UDJdwSLY0ERyUiry\niNl1fVr3oswGMRyuSfpAHjpAd1rLDs3gOFP28vDCUL1I766jbApaJGygkCjvxmxQ\nGWIdndYxFcrAak0eqy2ZP8qFH0qZA7JKqNSIhw8fCCZ8F54LceKVAHdSOxL+i4b+\nLcc5g+vfgvViBdJWSAd1DHFf\n-----END PRIVATE KEY-----\n" :
            "-----BEGIN PRIVATE KEY-----\nMIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQCZXP7jo93HHaaU\n3vqbnHxAnbjRcBoTnNJhsnJRIkEQGjOZAwfQJdTjIjsEHuPf0W0m02DRSj47S2l7\ngTWtkirkTdCQO48z8t4r8wG4yG5/GplAZz1vLc4Mfmv4WRSYdDoCVAtFeqSQGWwH\nsvjplxiGppGnLILl08FsEG3PBrU8uv0E6Sgrv8Al453XaJrI2T38uvxG15yIyZYl\nyGYMpD4HLxpWcZqRH3GIqDg5gBndkd34zcwDW6TYQEvf+XWnSeE8Rvf1JlXOFho+\nmOdU1IE+ETvmEwll6F01okKoRuvC67IfkmuGiieENXRve7NLncqPYUwX2TiE7H2X\n9CctJVYFAgMBAAECggEAG8xOayadoWTPd/Z+yEvx/ahZAqVmJJ3tSEYXEHu7/VYy\neg6hkOn6j3z544JR3ZTRSxNPXkAtzYOayAcgmqh+28j6/9m72Deya5btfffeh21j\nz9emvAs6aOtVWLuUzxlqFlfzfxF+2JuzCCY5leWMTYPNrNaVl2Dtk0/bB4Z4vVwD\nX4X9NFShUEIQzghlrFGdht1nztluOX+1ZGnhfhf5FfbZtSlOR3UlUuSjvaEWMAhg\nghHru/s8PMdsDusTMRv3tgjbLac70nTYsmOdw7eIaEM16WeU0McHSNsaA7h8Nm7u\nsyS38HpgvDlRF6uWDq8qD1x8HSGhORVTLEQWEDEYNQKBgQDSpwjysfYjW77Kx17v\nB0ZYViroV1RHEPXLxhbPT4z+x7eor8KW7JtHSnKUY5bnuswbYq6Sq/vjizH8V8Hq\nFvdI+YyXzU+mBB0Hjat/4PDp8IDFQ/Gte7fVHIFYlqmHO9pUfZqPjqqnTVBOj0a3\nYLGcoFD+NTcCZkrbokFrvesmFwKBgQC6YMcAIYqQHslcudo5BtTS5CfuQnTOoJS8\nV3oemCofHgV4Ps8uCa/16s8yNwAQpMD21kpfYVpGyQtGicQICUhtPtu1uOMW7924\n0eShM4LIa9nnexHnCbi1cXTQUdMvPBGJCLZuAz60+p3qkZjgiuNe6Dhr2VRMWDPv\nNL/vhDFSQwKBgQCpr9QNbD2SDf2K6z8V3Q5ValP2BSLmfEsDcvY82SACvG69e5eM\na9xJeTcDQZX15Qs1pDBsBg5KJX6Gf0q9fvKD3kHappBac0XPJ9fWkyR9zjZssWal\nENvs16F58yNsxFIDbYpryci+LBFctx2H6mQofxu/Kq69SBlVA0cfu0UI0QKBgHjD\nto5GLYN+/1i+8ECdRY+02I7QruEoOdfkErP6QujQyhiFEjexfTuqP0E5VCh0qLgI\nWD5/VUCiilTVdZkDctOetDyGpVa6a/+SuUMennKBN7t/CjWbHf0rm9ZT/rQPhwk1\nBQg5wAsCjQpt9xIfzH41mEGBm9awHE617lwjWtYDAoGBAKSQ8+sa/YelAXcqc2J/\nUQ5Bqf7z0mxUCMIrtAc1Vt7V+xXlCHAG050adXZJ56tSqHVOINn4xn7ZU0yjxkEg\nRUYLahOK4jjBIrSZLZe0Osyu/HoD6o3VxlE/PJVX9Ag2xSgJL28JLBSN/5JOh+0v\nF1Hjm/gg+H17pFSNHRNQVv+6\n-----END PRIVATE KEY-----\n";

        try {
            const doc = new GoogleSpreadsheet(sheetId);
            await doc.useServiceAccountAuth({ client_email: clientEmail, private_key: privateKey });
            await doc.loadInfo();
            const sheet = doc.sheetsById["0"];
            const newRow = {
                OrderID: orderId,
                Date: date,
                FirstName: fname,
                LastName: lname,
                Group_Number: gn,
                Email: email,
                Address1: address1,
                Address2: address2,
                City: city,
                State: state,
                Zip: zip,
                CardsQuantity: quantity,
                CardType: cardStyle,
                Phone: phone,
                ProductName: '-',
                ProductId: '-',
                Price: '-',
                Quantity: '-',
                Pharmacy: '-',
                Size: '-'
            };
            await sheet.addRow(newRow);
        } catch (e) {
            console.error('Error submitting to Google Sheets:', e);
        }
    };

    const handleClose = (e) => {
        if (e) e.stopPropagation();
        if (toggle_user_detail) toggle_user_detail(e || {});
    };

        return (
        <div className="w-full">
            {/* Header */}
            <DialogHeader className="px-6 py-4 border-b">
                <div className="flex items-center justify-between">
                    <DialogTitle>NuLifeSpan Rx Marketplace</DialogTitle>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClose}
                        className="h-8 w-8"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                        </div>
            </DialogHeader>

            {/* Content */}
            <div className="p-6 space-y-6">
                {currentStep === 'success' ? (
                    /* Success Message */
                    <div className="text-center py-12">
                        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h3 className="text-2xl font-semibold mb-2">Thank you for your payment.</h3>
                        <p className="text-muted-foreground">
                            You will receive an email with your receipt and shipping details
                        </p>
                        </div>
                ) : (
                    <>
                        {/* Card Details Step */}
                        {forCard && currentStep === 'card-details' && (
                            <div className="space-y-4">
                                <h3 className="text-base font-semibold">Select Card Details</h3>
                                    <div className="space-y-2">
                                        <Label htmlFor="cardStyle">Card Style *</Label>
                                        <Select value={cardStyle} onValueChange={handleCardStyleChange}>
                                            <SelectTrigger className={errors.cardStyle ? 'border-destructive' : ''}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Rounded">Rounded</SelectItem>
                                                <SelectItem value="Square">Square</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {errors.cardStyle && (
                                            <p className="text-sm text-destructive">{errors.cardStyle}</p>
                                        )}
                        </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="quantity">Select Quantity *</Label>
                                        <Select value={quantity} onValueChange={handleQuantityChange}>
                                            <SelectTrigger className={errors.quantity ? 'border-destructive' : ''}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {QUANTITY_LIST.map(item => (
                                                    <SelectItem key={item.quantity} value={item.quantity.toString()}>
                                                        {item.quantity}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.quantity && (
                                            <p className="text-sm text-destructive">{errors.quantity}</p>
                                        )}
                        </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="groupNumber">Select Group No. *</Label>
                                        <Select value={groupNumber} onValueChange={setGroupNumber}>
                                            <SelectTrigger className={errors.groupNumber ? 'border-destructive' : ''}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {groupNumbers && groupNumbers.map(item => (
                                                    <SelectItem key={item.groupnumber} value={item.groupnumber}>
                                                        {item.groupnumber}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.groupNumber && (
                                            <p className="text-sm text-destructive">{errors.groupNumber}</p>
                                        )}
                                        </div>

                                    <div className="pt-4 border-t">
                                        <p className="text-lg font-semibold">
                                            Total Price: <span className="text-accent">${totalPrice}</span>
                                        </p>
                                                </div>

                                    <div className="flex justify-end">
                                        <Button
                                            onClick={handleNextFromCardDetails}
                                            className="bg-accent hover:!bg-accent text-white"
                                        >
                                            Next
                                        </Button>
                                    </div>
                            </div>
                        )}

                        {/* Shipping Step */}
                        {currentStep === 'shipping' && (
                            <div className="space-y-4">
                                <h3 className="text-base font-semibold">Ship To</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="fname">First Name *</Label>
                                            <Input
                                                id="fname"
                                                value={fname}
                                                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (/^[a-zA-Z]*$/.test(val)) setFname(val);
                                                }}
                                                className={errors.fname ? 'border-destructive' : ''}
                                                maxLength={20}
                                            />
                                            {errors.fname && (
                                                <p className="text-sm text-destructive">{errors.fname}</p>
                                            )}
                                                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="lname">Last Name *</Label>
                                            <Input
                                                id="lname"
                                                value={lname}
                                                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (/^[a-zA-Z]*$/.test(val)) setLname(val);
                                                }}
                                                className={errors.lname ? 'border-destructive' : ''}
                                                maxLength={20}
                                            />
                                            {errors.lname && (
                                                <p className="text-sm text-destructive">{errors.lname}</p>
                                            )}
                                                                        </div>
                                                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone Number *</Label>
                                        <Input
                                            id="phone"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className={errors.phone ? 'border-destructive' : ''}
                                        />
                                        {errors.phone && (
                                            <p className="text-sm text-destructive">{errors.phone}</p>
                                        )}
                                                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="address1">Address Line 1 *</Label>
                                        <Input
                                            id="address1"
                                            value={address1}
                                            onChange={(e) => setAddress1(e.target.value)}
                                            className={errors.address1 ? 'border-destructive' : ''}
                                            maxLength={25}
                                        />
                                        {errors.address1 && (
                                            <p className="text-sm text-destructive">{errors.address1}</p>
                                        )}
                                                                        </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="address2">Address Line 2</Label>
                                        <Input
                                            id="address2"
                                            value={address2}
                                            onChange={(e) => setAddress2(e.target.value)}
                                            className={errors.address2 ? 'border-destructive' : ''}
                                            maxLength={25}
                                        />
                                        {errors.address2 && (
                                            <p className="text-sm text-destructive">{errors.address2}</p>
                                        )}
                                                                        </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="state">State *</Label>
                                        <Select value={state} onValueChange={setState}>
                                            <SelectTrigger className={errors.state ? 'border-destructive' : ''}>
                                                <SelectValue placeholder="Please Select State" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {US_STATE_NAMES.map(stateName => (
                                                    <SelectItem key={stateName} value={stateName}>
                                                        {stateName}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.state && (
                                            <p className="text-sm text-destructive">{errors.state}</p>
                                        )}
                                                                        </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="city">City *</Label>
                                            <Input
                                                id="city"
                                                value={city}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (/^[a-zA-Z ]*$/.test(val)) setCity(val);
                                                }}
                                                className={errors.city ? 'border-destructive' : ''}
                                                maxLength={35}
                                            />
                                            {errors.city && (
                                                <p className="text-sm text-destructive">{errors.city}</p>
                                            )}
                                                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="zip">Zip/Postal Code *</Label>
                                            <Input
                                                id="zip"
                                                value={zip}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                                    setZip(val);
                                                }}
                                                className={errors.zip ? 'border-destructive' : ''}
                                                maxLength={5}
                                            />
                                            {errors.zip && (
                                                <p className="text-sm text-destructive">{errors.zip}</p>
                                            )}
                                                                        </div>
                                                                    </div>

                                    {errorMsg && (
                                        <Alert variant="destructive">
                                            <AlertDescription>{errorMsg}</AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="flex justify-end">
                                        <Button
                                            onClick={handleSaveShipping}
                                            className="bg-accent hover:!bg-accent text-white"
                                            disabled={loading}
                                        >
                                            {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving</> : 'Save & Continue'}
                                        </Button>
                                    </div>
                            </div>
                        )}

                        {/* Payment Step */}
                        {currentStep === 'payment' && (
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-base font-semibold">Shipping Address</h3>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setCurrentStep('shipping')}
                                            >
                                                Change
                                            </Button>
                                                                    </div>
                                    <address className="not-italic text-sm text-muted-foreground">
                                            {fname} {lname}<br />
                                            {address1} {address2}<br />
                                            {city}, {state}<br />
                                            USA, {zip}
                                                                    </address>
                                </div>

                                <div>
                                    <h3 className="text-base font-semibold mb-4">Payment Information</h3>
                                    <PaymentFormNew
                                        paymentForm={window.Square?.payments}
                                        Fname={fname}
                                        Lname={lname}
                                        Address1={address1}
                                        Address2={address2}
                                        State={state}
                                        City={city}
                                        Zip={zip}
                                        Email={email}
                                        toggle_Payment_pop={() => setCurrentStep('shipping')}
                                        SuccessMessage={handlePaymentSuccess}
                                        ProductName={cardStyle}
                                        quantity={quantity}
                                        group_number={groupNumber}
                                        base64blob={phone}
                                        forCard={forCard}
                                        total_price={totalPrice}
                                    />
                                </div>
                                                </div>
                        )}
                    </>
                )}
                                        </div>
                                    </div>
    );
}

export default OrderPage;

